import { create } from 'zustand'
import { workflowsApi } from '@/lib/api'
import { captureEvent } from '@/lib/posthog'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { planLimitToastMessage } from '@/lib/planLimitMessages'
import type { Workflow, WorkflowListItem, WorkflowStatus, WorkflowStats, WorkflowMember, CreateWorkflowData, UpdateWorkflowData } from '@/types/workflow'

interface WorkflowState {
  workflows: WorkflowListItem[]
  currentWorkflow: Workflow | null
  stats: WorkflowStats
  isLoading: boolean
  /** Roster for the "created by" filter; empty for roles that can't use it. */
  members: WorkflowMember[]
  /** Selected teammate, or null for "All members". */
  createdByUserId: number | null

  fetchWorkflows: (skip?: number, limit?: number) => Promise<void>
  fetchWorkflowById: (id: number) => Promise<Workflow>
  fetchStats: () => Promise<void>
  fetchMembers: () => Promise<void>
  /** Sets the filter only — the page refetches with it applied. */
  setCreatedByFilter: (userId: number | null) => void
  createWorkflow: (data: CreateWorkflowData) => Promise<Workflow>
  updateWorkflow: (id: number, data: UpdateWorkflowData) => Promise<Workflow>
  deleteWorkflow: (id: number) => Promise<void>
  /** Resolves to the workflow's resulting status -- "active" for an
   *  Owner/Admin/Manager's direct activation, or "pending_approval" when a
   *  Rep's request was queued instead (see backend/app/routes/workflows.py's
   *  activate_workflow). Callers must read this rather than assume "active". */
  // `approverId`: a Representative's chosen reviewer (ignored for direct roles).
  activateWorkflow: (id: number, approverId?: number | null) => Promise<WorkflowStatus>
  deactivateWorkflow: (id: number) => Promise<void>
  duplicateWorkflow: (id: number) => Promise<Workflow>
  setCurrentWorkflow: (workflow: Workflow | null) => void

  // Reset — wipes per-user data on account switch (see main.tsx auth subscribe).
  reset: () => void
}

const initialState = {
  workflows: [] as WorkflowListItem[],
  currentWorkflow: null as Workflow | null,
  stats: { total: 0, active: 0, paused: 0, draft: 0 } as WorkflowStats,
  isLoading: false,
  members: [] as WorkflowMember[],
  createdByUserId: null as number | null,
}

export const useWorkflowStore = create<WorkflowState>()((set, get) => ({
  ...initialState,

  fetchWorkflows: async (skip = 0, limit = 50) => {
    set({ isLoading: true })
    try {
      const createdBy = get().createdByUserId
      const response = createdBy != null
        ? await workflowsApi.getAll(skip, limit, createdBy)
        : await workflowsApi.getAll(skip, limit)
      const data = Array.isArray(response.data) ? response.data : []
      set({ workflows: data, isLoading: false })
    } catch (error) {
      set({ isLoading: false })
      toast.error('Failed to fetch workflows')
    }
  },

  fetchMembers: async () => {
    try {
      const response = await workflowsApi.getMembers()
      set({ members: Array.isArray(response.data) ? response.data : [] })
    } catch (error) {
      // Silent: the filter simply stays hidden if the roster can't be read.
    }
  },

  setCreatedByFilter: (userId: number | null) => set({ createdByUserId: userId }),

  fetchWorkflowById: async (id: number) => {
    set({ isLoading: true })
    try {
      const response = await workflowsApi.getById(id)
      set({ currentWorkflow: response.data, isLoading: false })
      return response.data
    } catch (error) {
      set({ isLoading: false })
      toast.error('Failed to fetch workflow')
      throw error
    }
  },

  fetchStats: async () => {
    try {
      const createdBy = get().createdByUserId
      const response = createdBy != null
        ? await workflowsApi.getStats(createdBy)
        : await workflowsApi.getStats()
      set({ stats: response.data })
    } catch (error) {
      // silently fail
    }
  },

  createWorkflow: async (data: CreateWorkflowData) => {
    try {
      const response = await workflowsApi.create(data)
      const { workflows } = get()
      set({ workflows: [response.data, ...workflows] })
      captureEvent('workflow_created', { workflow_id: response.data.id })
      toast.success('Workflow created successfully')
      return response.data
    } catch (error: any) {
      // The API call sets skipErrorToast, so this is the ONLY toast for any
      // create failure (409 "Workflow with same name exists!", the trial's
      // 5-workflow cap, or anything else) — planLimitToastMessage falls back
      // to the real detail (or this generic message) for the non-plan-limit
      // case, so nothing that used to surface here goes silent.
      const isTrial = useAuthStore.getState().planStatus?.is_trial ?? false
      const { message, showUpgrade } = planLimitToastMessage(error, { isTrial, fallback: 'Failed to create workflow' })
      toast.error(message, showUpgrade ? { action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing' } } } : undefined)
      throw error
    }
  },

  updateWorkflow: async (id: number, data: UpdateWorkflowData) => {
    try {
      const response = await workflowsApi.update(id, data)
      set({ currentWorkflow: response.data })
      // Also update in list
      const { workflows } = get()
      set({ workflows: workflows.map(w => w.id === id ? { ...w, ...response.data } : w) })
      return response.data
    } catch (error: any) {
      // The axios interceptor already toasts errors that carry a backend
      // `detail` (e.g. the 409 "Workflow with same name exists!"); avoid a
      // duplicate by only handling the detail-less case here.
      if (!error?.response?.data?.detail) {
        toast.error('Failed to save workflow')
      }
      throw error
    }
  },

  deleteWorkflow: async (id: number) => {
    try {
      await workflowsApi.delete(id)
      const { workflows } = get()
      set({ workflows: workflows.filter(w => w.id !== id) })
      toast.success('Workflow deleted')
    } catch (error) {
      // Error toast is shown globally by the axios response interceptor.
      throw error
    }
  },

  activateWorkflow: async (id: number, approverId?: number | null) => {
    try {
      const response = await workflowsApi.activate(id, approverId)
      const resultStatus: WorkflowStatus = response.data.status
      const { workflows } = get()
      set({
        workflows: workflows.map(w => w.id === id ? { ...w, status: resultStatus } : w),
        currentWorkflow: get().currentWorkflow?.id === id ? response.data : get().currentWorkflow
      })
      toast.success(
        resultStatus === 'pending_approval'
          ? 'Sent for approval. You will be notified once it is reviewed.'
          : 'Workflow activated',
      )
      return resultStatus
    } catch (error: any) {
      // Activation can now fail on the PLAN as well as on structure: a
      // workflow using a step the tier does not include (A/B Test, AI
      // Collaterals, Slack/Teams Team Alert, CRM Sync) is refused with a
      // PlanGateError. Routed through planLimitToastMessage so that one
      // gets an Upgrade action instead of being a dead end -- every other
      // activation failure still surfaces its own detail, unchanged.
      const isTrial = useAuthStore.getState().planStatus?.is_trial ?? false
      const { message, showUpgrade } = planLimitToastMessage(error, { isTrial, fallback: 'Failed to activate workflow' })
      toast.error(message, showUpgrade ? { action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing' } } } : undefined)
      throw error
    }
  },

  deactivateWorkflow: async (id: number) => {
    try {
      const response = await workflowsApi.deactivate(id)
      const { workflows } = get()
      set({
        workflows: workflows.map(w => w.id === id ? { ...w, status: 'paused' } : w),
        currentWorkflow: get().currentWorkflow?.id === id ? response.data : get().currentWorkflow
      })
      toast.success('Workflow paused')
    } catch (error) {
      toast.error('Failed to pause workflow')
      throw error
    }
  },

  duplicateWorkflow: async (id: number) => {
    try {
      const response = await workflowsApi.duplicate(id)
      const { workflows } = get()
      set({ workflows: [response.data, ...workflows] })
      toast.success('Workflow duplicated')
      return response.data
    } catch (error) {
      toast.error('Failed to duplicate workflow')
      throw error
    }
  },

  setCurrentWorkflow: (workflow) => set({ currentWorkflow: workflow }),

  reset: () => set(initialState),
}))
